// Rhythmic accuracy from a microphone: find the hits in the input, line them up with the notes
// that were playing, and score how close each hit came. Everything here is plain math on times
// (in seconds, on the audio clock), so it runs the same in the browser and in tests.

const HIGH_PASS_HZ = 120;

// The audio worklet: for each 128-sample block of microphone input, the loudest sample (after a
// high-pass filter that removes rumble) and its time. Hit detection runs on the main thread.
export const PEAKS_WORKLET = `
class TrueChopsPeaks extends AudioWorkletProcessor {
  constructor() { super(); this.previousIn = 0; this.previousOut = 0; this.alpha = Math.exp(-2 * Math.PI * ${HIGH_PASS_HZ} / sampleRate); }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) {
      let peak = 0, at = 0;
      for (let i = 0; i < channel.length; i++) {
        const out = this.alpha * (this.previousOut + channel[i] - this.previousIn);
        this.previousIn = channel[i]; this.previousOut = out;
        const level = out < 0 ? -out : out;
        if (level > peak) { peak = level; at = i; }
      }
      this.port.postMessage([(currentFrame + at) / sampleRate, peak]);
    }
    return true;
  }
}
registerProcessor("truechops-peaks", TrueChopsPeaks);`;

// The same per-block peaks for recorded samples (tests and offline checks).
export function blockPeaks(samples, sampleRate, block = 128) {
  const alpha = Math.exp(-2 * Math.PI * HIGH_PASS_HZ / sampleRate), peaks = [];
  let previousIn = 0, previousOut = 0;
  for (let start = 0; start < samples.length; start += block) {
    let peak = 0, at = 0;
    for (let i = start; i < Math.min(samples.length, start + block); i++) {
      const out = alpha * (previousOut + samples[i] - previousIn);
      previousIn = samples[i]; previousOut = out;
      if (Math.abs(out) > peak) { peak = Math.abs(out); at = i - start; }
    }
    peaks.push([(start + at) / sampleRate, peak]);
  }
  return peaks;
}

// Finds hits in the stream of block peaks: a peak above `threshold` that jumps to at least `rise`
// times the last few blocks (a new attack, not a ringing drum), and not right after another hit.
export function createHitDetector({ threshold = 0.03, rise = 1.5, refractory = 0.04, history = 3 } = {}) {
  const recent = [];
  let last = -Infinity;
  return (time, peak) => {
    // An attack can straddle two blocks, so compare with the blocks before the previous one.
    const before = recent.length > 1 ? Math.max(...recent.slice(0, -1)) : 0;
    recent.push(peak);
    if (recent.length > history + 1) recent.shift();
    if (peak >= threshold && peak >= before * rise && time - last >= refractory) { last = time; return { time, level: peak }; }
    return null;
  };
}

// Scores one take: the notes that sounded (`expect`, as playback schedules them) against the hits
// heard (`hear`), less the device's latency (`offset`, from calibration). Notes sounding together
// (a chord, a flam's grace note) count as one. Each note is matched to the closest hit within a
// window that shrinks for fast passages; `settle(now)` scores notes once no later hit could match.
export const PERFECT = 0.015, ON_TIME = 0.03;
export function createTakeScorer({ offset = 0 } = {}) {
  const notes = [], hits = [];
  let extra = 0, settled = 0;
  const windowAt = (index) => {
    const note = notes[index], gaps = [notes[index - 1] && note.time - notes[index - 1].time, notes[index + 1] && notes[index + 1].time - note.time].filter(Boolean);
    return Math.min(0.1, Math.max(0.03, (gaps.length ? Math.min(...gaps) : 0.2) * 0.45));
  };
  function settle(now = Infinity) {
    while (settled < notes.length) {
      const note = notes[settled], window = windowAt(settled);
      // Wait for the next note (it sets the window) and for any late hit to arrive.
      if (now !== Infinity && (!notes[settled + 1] || now < note.time + window + 0.05)) break;
      let best = null;
      for (const hit of hits) if (!hit.used && Math.abs(hit.time - note.time) <= window && (!best || Math.abs(hit.time - note.time) < Math.abs(best.time - note.time))) best = hit;
      if (best) { best.used = true; note.error = best.time - note.time; } else note.error = null;
      note.window = window;
      settled++;
    }
    // Hits that matched nothing and are now too old to match anything are extra strokes.
    // Only strokes during the take count (not warming up before it or carrying on after it).
    const horizon = settled < notes.length ? notes[settled].time - 0.15 : now;
    const first = (notes[0]?.time ?? Infinity) - 0.15, last = (notes[notes.length - 1]?.time ?? -Infinity) + 0.15;
    for (let i = hits.length - 1; i >= 0; i--) if (hits[i].time < horizon) { if (!hits[i].used && hits[i].time > first && hits[i].time < last) extra++; hits.splice(i, 1); }
  }
  return {
    expect(time, weight = 1) {
      const last = notes[notes.length - 1];
      if (last && Math.abs(time - last.time) < 0.025) { if (weight > last.weight) { last.time = time; last.weight = weight; } return; }
      notes.push({ time, weight, error: undefined });
    },
    hear(time) { hits.push({ time: time - offset, used: false }); },
    settle,
    // The take so far: notes scored, hits, misses, extra strokes, how many were on time, the
    // average timing (positive = late) and spread, and accuracy out of 100.
    summary() {
      const scored = notes.slice(0, settled), matched = scored.filter((note) => note.error != null);
      if (scored.length < 4) return { notes: scored.length, hits: matched.length, missed: scored.length - matched.length, extra, onTime: 0, meanOffset: null, spread: null, accuracy: null };
      const points = scored.reduce((sum, note) => sum + (note.error == null ? 0 : Math.abs(note.error) <= PERFECT ? 1 : Math.max(0, 1 - (Math.abs(note.error) - PERFECT) / (note.window - PERFECT))), 0);
      const errors = matched.map((note) => note.error), mean = errors.length ? errors.reduce((a, b) => a + b, 0) / errors.length : null;
      return {
        notes: scored.length, hits: matched.length, missed: scored.length - matched.length, extra,
        onTime: matched.filter((note) => Math.abs(note.error) <= ON_TIME).length,
        meanOffset: mean == null ? null : Math.round(mean * 1000),
        spread: mean == null ? null : Math.round(Math.sqrt(errors.reduce((sum, error) => sum + (error - mean) ** 2, 0) / errors.length) * 1000),
        accuracy: Math.round(100 * Math.max(0, points - extra * 0.5) / scored.length),
      };
    },
  };
}

// Calibration by the device's own ear: clicks played through the speakers (`clicks`, their times)
// and heard back by the microphone (`peaks`, its [time, peak] blocks). The delay from a click being
// played to it being heard is the delay to take off a player's hits, since they play with what they
// hear and what they play comes in the same way. Each click's arrival is the first block after it
// that's well over the room's noise (`noisePeak`) and rising. Most clicks must arrive alike (an
// echo or a bump can't pass for the delay). Returns the median delay, how many clicks were heard,
// the hit threshold (over the room, and over the speakers as the microphone hears them), and whether
// the speakers are loud enough in the microphone to pass for hits.
export const ARRIVAL_SPREAD = 0.012;
export function measureLoopback({ clicks, peaks, noisePeak = 0 }) {
  const floor = Math.max(noisePeak * 4, 0.006), arrivals = [];
  for (const click of clicks) {
    const recent = [];
    for (const [time, peak] of peaks) {
      if (time < click - 0.03) continue;
      if (time > click + 0.5) break;
      if (time >= click && peak >= floor && peak >= Math.max(0, ...recent) * 3) { arrivals.push({ delay: time - click, level: peak }); break; }
      recent.push(peak); if (recent.length > 3) recent.shift();
    }
  }
  const median = (values) => [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const middle = arrivals.length ? median(arrivals.map((arrival) => arrival.delay)) : 0, alike = arrivals.filter((arrival) => Math.abs(arrival.delay - middle) <= ARRIVAL_SPREAD);
  if (alike.length < Math.ceil(clicks.length * 0.6)) return { ok: false, heard: alike.length,
    reason: "The microphone didn't hear the clicks clearly. With headphones on it can't: take them off for a moment, or turn your speakers up, and try again. Or line the clicks up by ear." };
  const level = median(alike.map((arrival) => arrival.level));
  return { ok: true, offset: Math.round(median(alike.map((arrival) => arrival.delay)) * 1000) / 1000, heard: alike.length,
    threshold: Math.round(Math.min(0.2, Math.max(noisePeak * 3, level * 1.2, 0.01)) * 10000) / 10000, speakersTooLoud: level > 0.12 };
}
